import React, { useEffect, useState } from "react";
import { BREAKPOINT, COLORS } from "utils/styles";

import { Button } from "react-bootstrap";
import ArticlePost from "components/Posts/ArticlePost";
import MusicPost from "components/Posts/MusicPost";
import {
  getAudienceEmoji,
  POST_TYPES,
  NARRATIVE_TYPES,
  AUDIENCE_TYPES,
  getAudienceSlug,
} from "posts/constants";
import PhotoPost from "components/Posts/PhotoPost";
import TvPost from "components/Posts/TvPost";
import TwitterPost from "components/Posts/TwitterPost";
import VideoPost from "components/Posts/VideoPost";
import styled from "styled-components";
import { scroller } from "react-scroll";
import slugify from "slugify";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import {
  collapseAnimationDuration,
  collapseAnimationEasing,
} from "components/Feed/styles";
import MarkdownStrong from "./MarkdownStrong";
import usePrevious from "hooks/usePrevious";
import Pager from "components/Pager";
import isEqual from "lodash.isequal";
import { useRouter } from "next/router";

const Wrapper = styled.div`
  background: #fff;
  transition: all ${collapseAnimationDuration} ${collapseAnimationEasing};
  margin-left: ${({ isCollapsed }) => {
    return isCollapsed ? "0" : "40vw";
  }};

  @media ${BREAKPOINT.M} {
    display: block;
    margin-left: 0;
  }
`;

const EmptyWrapper = styled.div`
  margin-top: 5rem;
`;

const FeedWrapper = styled.div`
  background: #f9f9f9;
  padding: 3rem 1rem 0 1rem;

  @media ${BREAKPOINT.M} {
    padding-bottom: 30vh;
  }
`;

const PostWrapper = styled.div`
  position: relative;
`;

const InfoPanelWrapper = styled.div`
  background: #fff;
  height: 100%;
  width: 40vw;
  left: 0;
  top: 0;
  bottom: 0;
  position: relative;
  z-index: 10;

  @media ${BREAKPOINT.M} {
    height: 40vh;
    width: 100vw;
    top: auto;
    z-index: 100;
    position: fixed;
    bottom: 0;
    right: 0;
    border-top-left-radius: 25px;
    border-top-right-radius: 25px;
    box-shadow: 1.5px -2px 15px 0px rgba(0, 0, 0, 0.1);
  }
`;

const InfoPanelInnerWrapper = styled.div`
  width: 40vw;
  top: 8rem;
  bottom: 0;
  padding: 0 2rem;
  overflow: scroll;
  position: fixed;
  transition: all ${collapseAnimationDuration} ${collapseAnimationEasing};
  left: ${({ isCollapsed }) => {
    return isCollapsed ? "-40vw" : "0";
  }};

  display: flex;
  align-items: start;
  justify-content: center;
  flex-direction: column;

  @media ${BREAKPOINT.M} {
    width: 100vw;
    padding: 2rem;
    position: static;
    height: 100%;
  }
`;

const InfoPanelContent = styled.div`
  transition: all 350ms ease-out;
  opacity: ${({ isInsightFocused }) => (isInsightFocused ? 1 : 0.25)};
  padding-bottom: 7rem;

  h1 {
    margin-bottom: 2rem;
  }

  em {
    font-family: "mabry-light-italic" !important;
  }

  img {
    max-width: 100%;
    min-height: 240px;
    background: #ddd;
    object-fit: scale-down;
  }

  a {
    display: inline-block;
    color: #212529;
    text-decoration: none;
    border-bottom: 1px solid #999;
    line-height: 1.2;

    &:hover,
    &:focus {
      color: #000;
      border-bottom: 1px solid #000;
    }
  }
  @media ${BREAKPOINT.M} {
    h1 {
      font-size: 1.5rem;
    }
  }
`;

const TagWrapper = styled.div`
  margin-bottom: 1rem;
`;
const Tag = styled.div`
  font-family: "mabry-regular", sans-serif;
  font-weight: 400;
  i {
    transform: translateY(4px);
  }
`;
const AudienceButtonWrapper = styled.div`
  display: flex;
  margin-bottom: 1rem;
  .btn {
    margin-left: 1rem;
    &:first-child {
      margin-left: 0;
    }
  }
`;

const NoPostsMessage = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  align-content: center;
  justify-content: center;
`;

const NoPostsTitle = styled.span.attrs({ className: "mabry-medium" })`
  display: block;
  font-size: 1.25rem;
  text-align: center;
`;

const NoPostsSubtitle = styled.span`
  display: block;
  font-size: 1rem;
  text-align: center;
`;

function getAudienceWidth(audience) {
  switch (audience) {
    case "People Power": {
      return 160;
    }
    case "If You Say So": {
      return 155;
    }
    case "Tough Cookies": {
      return 170;
    }
    case "Don't Tread on Me": {
      return 196;
    }
  }
}

export default function Feed(props) {
  const { currentAudience, children, filters } = props;
  const [hasScrolled, setHasScrolled] = useState(false);
  const [insights, setInsights] = useState([]);
  const [actualCurrentInsight, setCurrentInsight] = useState(-1);
  const currentInsight = hasScrolled
    ? actualCurrentInsight
    : currentAudience
    ? 0
    : -1;
  const [isInsightFocused, setIsInsightFocused] = useState(true);
  const [posts, setPosts] = useState(props.posts);
  const previousFilters = usePrevious(filters);
  const router = useRouter();

  const setActiveAudience = (audience) => {
    if (audience) {
      router.push(`/feed/${getAudienceSlug(audience)}`);
    } else {
      router.push("/feed/");
    }
  };

  useEffect(() => {
    if (isEqual(filters, previousFilters)) return;
    if (insights.length === 0) setCurrentInsight(-1);
  }, [filters, insights]);

  useEffect(() => {
    if (!currentAudience) {
      setPosts(props.posts);
      return;
    }
    setPosts(
      props.posts.filter((p) => {
        if (!p.audiences || !p.audiences.length) return false;
        return p.audiences.includes(currentAudience);
      })
    );
  }, [props.posts, currentAudience]);

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(true);
      window.removeEventListener("scroll", handleScroll);
    };

    if (process.browser) {
      window.addEventListener("scroll", handleScroll);
    }
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const getPostId = (post, i) => {
    return `post-${post.type}-${
      currentAudience ? getAudienceSlug(currentAudience) + "-" : ""
    }${i}`;
  };
  const getInsightIndex = (post, id) => {
    const insight = insights.find((p) => p.id === id);
    const idx = insight?.index;
    if (idx > -1) {
      return idx;
    }
    return -1;
  };
  useEffect(() => {
    // The ordering here is important because we need to know the post
    // index to create the id. If we filter before we create the insights
    // array, the index will be off bc it won't include posts that have
    // been filtered out.
    setInsights(
      posts
        .map((p, i) => ({
          id: getPostId(p, i),
          content: p.insights,
        }))
        .filter((p) => p.content)
        .map((p, i) => ({
          ...p,
          index: i,
        }))
    );
  }, [posts]);

  const handleInsightPostEnterView = (post, id, inView) => {
    if (!hasScrolled) return;
    const current = getInsightIndex(post, id);
    if (inView) {
      if (current > -1) {
        setCurrentInsight(current);
      }
      setIsInsightFocused(true);
    } else {
      if (current === currentInsight) {
        setIsInsightFocused(false);
      }
    }
  };

  const handleAudienceClick = (audience) => {
    setActiveAudience(audience);
  };

  useEffect(() => {
    if (currentAudience) {
      setHasScrolled(true);
      setCurrentInsight(0);
    } else {
      setHasScrolled(false);
      setCurrentInsight(-1);
    }
  }, [currentAudience]);

  const renderZeroState = () => {
    return (
      <InfoPanelContent isInsightFocused={true}>
        <TagWrapper>
          <Tag>
            <i className="material-icons-round md-dark md-18">trending_up</i>{" "}
            Stories &amp; Opportunities
          </Tag>
        </TagWrapper>
        <h1>The Media Feed</h1>
        <p>
          Step inside a “day in the media life” of our 4 values-distinct
          audiences. These are real-life media artifacts collected from 50,000+
          Americans between January 2020 and May 2021.{" "}
          <a href="/about" target="blank">
            Learn more
          </a>{" "}
          about how we did this.{" "}
        </p>
        <p>
          Use the arrow keys to see key insights and spot specific story risks
          and opportunities.{" "}
        </p>
        <br />
        <p>
          <strong>Select</strong> an Audience to filter the stories.
        </p>
        <AudienceButtonWrapper>
          <Button
            onClick={() => {
              handleAudienceClick(AUDIENCE_TYPES.PEOPLE_POWER);
            }}
            variant="light"
          >
            {getAudienceEmoji(AUDIENCE_TYPES.PEOPLE_POWER)} People Power
          </Button>
          <Button
            onClick={() => {
              handleAudienceClick(AUDIENCE_TYPES.IF_YOU_SAY_SO);
            }}
            variant="light"
          >
            {getAudienceEmoji(AUDIENCE_TYPES.IF_YOU_SAY_SO)} If You Say So
          </Button>
        </AudienceButtonWrapper>
        <AudienceButtonWrapper>
          <Button
            onClick={() => {
              handleAudienceClick(AUDIENCE_TYPES.TOUGH_COOKIES);
            }}
            variant="light"
          >
            {getAudienceEmoji(AUDIENCE_TYPES.TOUGH_COOKIES)} Tough Cookies
          </Button>
          <Button
            onClick={() => {
              handleAudienceClick(AUDIENCE_TYPES.DONT_TREAD_ON_ME);
            }}
            variant="light"
          >
            {getAudienceEmoji(AUDIENCE_TYPES.DONT_TREAD_ON_ME)} Don&apos;t Tread
            on Me
          </Button>
        </AudienceButtonWrapper>
      </InfoPanelContent>
    );
  };

  const renderPost = (post, i) => {
    const id = getPostId(post, i);
    const shouldTrackInsights = Boolean(post.insights);
    const activeInsight = insights[currentInsight] || {};
    const postIsFocused = isInsightFocused && id === activeInsight.id;
    switch (post.type) {
      case POST_TYPES.ARTICLE:
        return (
          <ArticlePost
            key={id}
            id={id}
            name={id}
            activeAudience={currentAudience}
            showPostFooter={true}
            {...post}
            filters={filters}
            shouldTrackInsights={shouldTrackInsights}
            onInsightPostEnterView={(inView) =>
              handleInsightPostEnterView(post, id, inView)
            }
            anyPostIsFocused={isInsightFocused}
            postIsFocused={postIsFocused}
          />
        );
      case POST_TYPES.VIDEO:
        return (
          <VideoPost
            key={id}
            id={id}
            name={id}
            activeAudience={currentAudience}
            showPostFooter={true}
            {...post}
            filters={filters}
            shouldTrackInsights={shouldTrackInsights}
            onInsightPostEnterView={(inView) =>
              handleInsightPostEnterView(post, id, inView)
            }
            anyPostIsFocused={isInsightFocused}
            postIsFocused={postIsFocused}
          />
        );
      case POST_TYPES.PHOTO:
        return (
          <PhotoPost
            key={id}
            id={id}
            name={id}
            activeAudience={currentAudience}
            showPostFooter={true}
            {...post}
            filters={filters}
            shouldTrackInsights={shouldTrackInsights}
            onInsightPostEnterView={(inView) =>
              handleInsightPostEnterView(post, id, inView)
            }
            anyPostIsFocused={isInsightFocused}
            postIsFocused={postIsFocused}
          />
        );
      case POST_TYPES.MUSIC:
        return (
          <MusicPost
            key={id}
            id={id}
            name={id}
            activeAudience={currentAudience}
            showPostFooter={true}
            {...post}
            filters={filters}
            shouldTrackInsights={shouldTrackInsights}
            onInsightPostEnterView={(inView) =>
              handleInsightPostEnterView(post, id, inView)
            }
            anyPostIsFocused={isInsightFocused}
            postIsFocused={postIsFocused}
          />
        );
      case POST_TYPES.TWITTER:
        return (
          <TwitterPost
            key={id}
            id={id}
            name={id}
            activeAudience={currentAudience}
            showPostFooter={true}
            {...post}
            filters={filters}
            shouldTrackInsights={shouldTrackInsights}
            onInsightPostEnterView={(inView) =>
              handleInsightPostEnterView(post, id, inView)
            }
            anyPostIsFocused={isInsightFocused}
            postIsFocused={postIsFocused}
          />
        );
      case POST_TYPES.TV:
        return (
          <TvPost
            key={id}
            id={id}
            name={id}
            activeAudience={currentAudience}
            showPostFooter={true}
            {...post}
            filters={filters}
            shouldTrackInsights={shouldTrackInsights}
            onInsightPostEnterView={(inView) =>
              handleInsightPostEnterView(post, id, inView)
            }
            anyPostIsFocused={isInsightFocused}
            postIsFocused={postIsFocused}
          />
        );
      default:
        return null;
    }
  };

  const getInsightOffset = (id) => {
    const $insight = document.querySelector(`#${id}`);
    if (!$insight || !window) {
      return -80;
    }
    const insightHeight = $insight.getBoundingClientRect().height;
    const windowHeight = window.innerHeight;
    const offset = (windowHeight / 2 - insightHeight / 2) * -1;

    return offset;
  };

  function handleInsightClick(val = 1) {
    const next = currentInsight + val;
    setCurrentInsight(next);
    if (next > -1) {
      setIsInsightFocused(true);
      const insight = insights[next];
      setHasScrolled(true);

      scroller.scrollTo(`${insight.id}`, {
        duration: 500,
        delay: 0,
        offset: getInsightOffset(insight.id),
        smooth: false,
      });
    } else {
      setActiveAudience(null);
      setHasScrolled(false);
      setCurrentInsight(0);
    }
  }

  function getInsightContent() {
    // Wrap all audience/narratives with a bold tag. We will use a custom component
    // for all bold tags that will check for the presence of an audience/narrative name
    // to add more functionality via the ReactMarkdown component.
    const insight = insights[currentInsight];
    if (!insight) return;
    let content = insight.content || "";
    const audienceNames = Object.keys(AUDIENCE_TYPES).map(
      (k) => AUDIENCE_TYPES[k]
    );
    const narrativeNames = Object.keys(NARRATIVE_TYPES).map(
      (k) => NARRATIVE_TYPES[k]
    );
    const reString = [...audienceNames, ...narrativeNames]
      .reduce((acc, val) => {
        return `(${val})|${acc}`;
      }, "")
      .slice(0, -1);
    const re = new RegExp(
      `${audienceNames.join("|")}|${narrativeNames.join("|")}`,
      "ig"
    );
    content = content.replace(re, "**$&**");
    return content;
  }

  function clearAudienceFilter() {
    setActiveAudience(null);
  }

  useEffect(() => {
    // If the audience changes, reset the scroll state
    // and go back to the first insight.
    setHasScrolled(false);
    setCurrentInsight(0);
  }, [currentAudience]);

  if (posts && posts.length) {
    return (
      <div>
        <Wrapper isCollapsed={props.isCollapsed}>
          <InfoPanelWrapper>
            <InfoPanelInnerWrapper isCollapsed={props.isCollapsed}>
              {currentInsight > -1 ? (
                <InfoPanelContent isInsightFocused={isInsightFocused}>
                  <ReactMarkdown
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      strong: MarkdownStrong,
                    }}
                  >
                    {getInsightContent()}
                  </ReactMarkdown>
                </InfoPanelContent>
              ) : (
                renderZeroState()
              )}
              <Pager
                isCollapsed={props.isCollapsed}
                nextDisabled={currentInsight === insights.length - 1}
                previousDisabled={currentInsight === -1}
                next={() => handleInsightClick(1)}
                previous={() => handleInsightClick(-1)}
                current={currentInsight + 1}
                total={insights.length}
              />
            </InfoPanelInnerWrapper>
          </InfoPanelWrapper>
          <FeedWrapper>
            {posts.map((post, i) => {
              return (
                <PostWrapper
                  key={`post-wrapper-${i}`}
                  className={Boolean(post.insights) ? "has-insights" : ""}
                  hasInsights={Boolean(post.insights)}
                >
                  {renderPost(post, i)}
                </PostWrapper>
              );
            })}
          </FeedWrapper>
        </Wrapper>
      </div>
    );
  }

  return (
    <Wrapper isCollapsed={props.isCollapsed}>
      <InfoPanelWrapper>
        <InfoPanelInnerWrapper isCollapsed={props.isCollapsed}>
          {currentInsight > -1 ? (
            <InfoPanelContent isInsightFocused={isInsightFocused}>
              <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                components={{
                  strong: MarkdownStrong,
                }}
              >
                {getInsightContent()}
              </ReactMarkdown>
            </InfoPanelContent>
          ) : (
            renderZeroState()
          )}
          <Pager
            isCollapsed={props.isCollapsed}
            nextDisabled={currentInsight === insights.length - 1}
            previousDisabled={currentInsight === -1}
            next={() => handleInsightClick(1)}
            previous={() => handleInsightClick(-1)}
            current={currentInsight + 1}
            total={insights.length}
          />
        </InfoPanelInnerWrapper>
      </InfoPanelWrapper>
      <FeedWrapper>
        <NoPostsMessage>
          <NoPostsTitle>No Posts Found</NoPostsTitle>
          <NoPostsSubtitle>
            Please adjust your filters or select a different audience.
          </NoPostsSubtitle>
        </NoPostsMessage>
      </FeedWrapper>
    </Wrapper>
  );
}
